import { ParagraphSitecore, ScrollLinkWrapper } from '@ads-core/components';
import { renderJssLink, renderJssRichText } from '@alliander-fe/jss-utils';
import {
  RichTextField,
  LinkField,
  withDatasourceCheck,
  TextField,
  ComponentRendering,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'src/jss/lib/component-props';
import React from 'react';
import { getLocale } from 'src/utils/getLocale';
import { useIntersection } from 'src/hooks/useIntersection';
import { AdditionalFields } from 'src/components/TableOfContentsContainer/types/TableOfContentsComponentRendering';
import { useDynamicThreshold } from 'src/hooks/useDynamicThreshold';

export type ParagraphPropsJssProps = ComponentProps & {
  fields: {
    language: TextField;
    text?: RichTextField;
    primaryButton: LinkField;
    secondaryButton: LinkField;
  };
  params?: {
    anchor?: string;
  };
  rendering: ComponentRendering & AdditionalFields;
};

const Paragraph = (props: ParagraphPropsJssProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const additionalFields = props.rendering.additionalFields;
  const threshold = useDynamicThreshold({ ref });

  // The intersection observer is used to set the intersecting state.
  // This way we can set the table of content item as active.
  useIntersection({
    callBack: (isIntersecting: boolean) => {
      if (isIntersecting && additionalFields?.anchor) {
        additionalFields?.isIntersecting(additionalFields?.anchor ?? '');
      }
    },
    ref,
    threshold: threshold,
  });

  const paragraphText = props.fields.text;
  const language = props.fields.language.value?.toString();
  const globalisedLanguage = getLocale(language);

  const text = renderJssRichText(paragraphText);
  const primaryButton = renderJssLink(props.fields.primaryButton);
  const secondaryButton = renderJssLink(props.fields.secondaryButton);

  if (!primaryButton && !secondaryButton && !text) {
    return null;
  }

  return (
    <div ref={ref}>
      <ScrollLinkWrapper anchorId={additionalFields?.anchor ?? props.params?.anchor ?? undefined}>
        <ParagraphSitecore
          language={globalisedLanguage}
          text={text}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
          isContained={Boolean(additionalFields?.isContained)}
        />
      </ScrollLinkWrapper>
    </div>
  );
};

export default withDatasourceCheck()<ComponentProps>(Paragraph);
